import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Bot, Check, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { BulkAcceptPanel } from '../components/BulkAcceptPanel'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { ProposalCard } from '../components/ProposalCard'
import { useAcceptProposals, useProposalSummary, useProposals, useSyncProposals } from '../hooks/useKnowledge'
import type { ProposalAction, SerRun } from '../api/knowledgeApi'

const PAGE = 30

const ACTIONS: Array<{ action: ProposalAction; label: string; what: string; bulk: boolean }> = [
  { action: 'merge', label: 'Merges', what: 'Concepts the model judges to be one concept. Accept to merge on the next build (you can fix the name); “Different” is remembered so it is never proposed again.', bulk: false },
  { action: 'split', label: 'Splits', what: 'Names that do not belong in their concept, judged with artwork thumbnails. Adjust the ticked names, then accept.', bulk: false },
  { action: 'link', label: 'Relations', what: 'Concepts that stay separate but are related: one broader than another, a variant, an opposite. Accepted relations appear on concept pages and go to search.', bulk: true },
  { action: 'category', label: 'Categories', what: 'A category for concepts that have none. Safe to accept in bulk; change any you disagree with first.', bulk: true },
  { action: 'generic', label: 'Generic', what: 'Concepts that say little about any one artwork. Accepting sets their search weight low; they stay in the graph.', bulk: true },
  { action: 'reject', label: 'Not concepts', what: 'Labels the model judges not to be real concepts, or not to fit the artworks they are on. Accepting removes them on the next build.', bulk: false },
]

function RunLine({ run }: { run: SerRun | null }) {
  if (!run) {
    return <p className="text-sm text-slate-500">No resolution run has reached this environment yet. It runs after the nightly graph build.</p>
  }
  const passed = run.calibrationPassed
  // Everything already judged (the verdict ledger): nothing was asked, so nothing to calibrate.
  // Runs before the Picasso fix (Oct 20) reported this as a failed calibration.
  const idle = !run.candidates && !run.verdicts && run.status !== 'running' && run.status !== 'error'
  if (idle) {
    return (
      <p className="text-sm text-slate-600">
        Last run {run.finishedAt ? formatDistanceToNow(new Date(run.finishedAt), { addSuffix: true }) : ''} found nothing new to
        judge — every concept group has a verdict. New concepts from the next build are judged the night they appear.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      <span>
        Last run {run.finishedAt ? formatDistanceToNow(new Date(run.finishedAt), { addSuffix: true }) : run.status}
        {run.model && <> · {run.model}</>}
      </span>
      <span>
        {run.verdicts?.toLocaleString() ?? 0} verdicts → {run.proposals?.toLocaleString() ?? 0} proposals
      </span>
      <span>
        ${(run.spentUsd ?? 0).toFixed(2)} of ${(run.budgetUsd ?? 0).toFixed(2)}
      </span>
      <span className={passed ? 'text-emerald-700' : 'text-rose-700'}>
        calibration {run.calibrationCatch != null ? `${Math.round(run.calibrationCatch * 100)}%` : '—'} {passed ? 'passed' : 'failed — no proposals kept'}
      </span>
    </div>
  )
}

export function ProposalsPage() {
  const [params, setParams] = useSearchParams()
  const action = (params.get('action') as ProposalAction) || 'merge'
  const domainId = params.get('domainId') || undefined
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data: summary } = useProposalSummary()
  const { data, isLoading, isFetching } = useProposals(action, 'pending', offset, PAGE, domainId)
  const acceptMany = useAcceptProposals()
  const sync = useSyncProposals()
  const meta = ACTIONS.find((a) => a.action === action)!

  useEffect(() => {
    setOffset(0)
    setSelected(new Set())
  }, [action, domainId])
  useEffect(() => setSelected(new Set()), [data])

  // While a new list loads, the previous one is shown; only render items of this list's action.
  const items = (data?.data ?? []).filter((p) => p.action === action)
  const total = data?.total ?? 0
  const count = (a: ProposalAction) => summary?.pending.find((x) => x.action === a)?.n ?? 0
  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  return (
    <KnowledgeFrame
      title="Proposals"
      description={
        <>
          After each nightly build a frontier model reviews groups of related concepts — names,
          the artworks they are on, what they appear with — and proposes merges, splits, relations,
          categories and removals. Nothing changes until you accept. Its calibration (planted cases it
          must catch) gates every run, and every verdict is kept, so it never asks the same question twice.
        </>
      }
    >
      {domainId && <p className="rounded-md border border-arthaus-gold/25 p-3 text-sm">Showing proposals for this research domain. <Link to="/app/knowledge/domains" className="underline">Back to domain agenda</Link></p>}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <RunLine run={summary?.latestRun ?? null} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/app/knowledge/agent"><Bot className="mr-1.5 h-3.5 w-3.5" /> Autopilot &amp; accept all</Link>
            </Button>
            <Button size="sm" variant="outline" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Pull latest
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.action}
            onClick={() => setParams(domainId ? { action: a.action, domainId } : { action: a.action })}
            className={`rounded-full border px-3 py-1 text-sm ${action === a.action ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
          >
            {a.label}
            <span className="ml-1.5 tabular-nums opacity-60">{count(a.action).toLocaleString()}</span>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{meta.label}</CardTitle>
          <CardDescription>{meta.what} Highest confidence first.</CardDescription>
          {count(action) > 0 && (
            <div className="pt-2">
              <BulkAcceptPanel action={action} label={meta.label} />
            </div>
          )}
          {meta.bulk && items.length > 0 && (
            <div className="flex items-center gap-2 pt-2 text-sm">
              <Checkbox
                checked={selected.size === items.length}
                onCheckedChange={(v) => setSelected(v ? new Set(items.map((p) => p.proposalId)) : new Set())}
                aria-label="Select all on this page"
              />
              <span className="text-slate-500">{selected.size ? `${selected.size} selected` : 'Select all on this page'}</span>
              {selected.size > 0 && (
                <Button size="sm" disabled={acceptMany.isPending} onClick={() => acceptMany.mutate([...selected])}>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Accept {selected.size}
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className={isFetching ? 'opacity-70' : undefined}>
          {isLoading && <Skeleton className="h-64 w-full" />}
          {!isLoading && items.length === 0 && <p className="py-8 text-center text-sm text-slate-500">No pending proposals here.</p>}
          {items.map((p) => (
            <ProposalCard
              key={p.proposalId}
              p={p}
              selectable={meta.bulk}
              selected={selected.has(p.proposalId)}
              onToggle={() => toggle(p.proposalId)}
            />
          ))}
          {total > PAGE && (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
                <Button size="sm" variant="outline" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </KnowledgeFrame>
  )
}
